import React from 'react';

type AlertVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'destructive';

const variantClass: Record<AlertVariant, string> = {
  default: 'bg-gray-50 border-gray-200 text-text-primary',
  success: 'bg-green-50 border-green-200 text-green-800',
  warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
  destructive: 'bg-red-50 border-red-200 text-red-800',
};

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: AlertVariant;
  severity?: 'success' | 'warning' | 'error' | 'info';
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
}

export const Alert: React.FC<AlertProps> = ({ variant, severity, children, className = '', onClose, ...rest }) => {
  const resolvedVariant: AlertVariant = variant || severity || 'default';
  const classes = variantClass[resolvedVariant] || variantClass.default;

  return (
    <div
      role="alert"
      className={`relative flex items-start gap-3 rounded-lg border p-4 ${classes} ${className}`}
      {...rest}
    >
      <div className="flex-1">{children}</div>
      {onClose && (
        <button
          onClick={onClose}
          className="flex-shrink-0 opacity-60 hover:opacity-100 transition-opacity"
          aria-label="閉じる"
        >
          ×
        </button>
      )}
    </div>
  );
};

export const AlertTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <p className={`font-semibold text-sm mb-1 ${className}`}>{children}</p>
);

export const AlertDescription: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <p className={`text-sm ${className}`}>{children}</p>
);
